/**
 * useConversations Hook
 * Manages conversation history fetching and loading
 */

import { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../config/api.js';

export function useConversations() {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Fetch all conversations for the current user
   */
  const fetchConversations = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No authentication token found');
      }

      console.log('📋 Fetching conversations...');
      
      const response = await fetch(`${API_BASE_URL}/api/conversations`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch conversations: ${response.status}`);
      }

      const data = await response.json();
      
      console.log(`✅ Loaded ${data.conversations.length} conversations`);
      
      setConversations(data.conversations);
      setError(null);
    } catch (err) {
      console.error('❌ Error fetching conversations:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Load a specific conversation by ID
   * Supports both conversationId (uses /api/chat/history) and sessionId (uses /api/conversations)
   */
  const loadConversation = useCallback(async (id, idType = 'auto') => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Auto-detect ID type: if it looks like a UUID, assume conversationId; otherwise sessionId
      let endpoint;
      if (idType === 'conversationId' || (idType === 'auto' && id.includes('-'))) {
        // Use conversation history endpoint
        endpoint = `${API_BASE_URL}/api/chat/history/${id}`;
        console.log(`📂 Loading conversation history by conversationId: ${id}`);
      } else {
        // Use conversations endpoint (by sessionId)
        endpoint = `${API_BASE_URL}/api/conversations/${id}`;
        console.log(`📂 Loading conversation by sessionId: ${id}`);
      }
      
      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to load conversation: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to load conversation');
      }
      
      const messages = data.messages || [];
      console.log(`✅ Loaded ${messages.length} messages from conversation`);
      
      // Transform messages to match expected format
      return messages.map(msg => {
        // Map 'assistant' role to 'ai' for frontend compatibility
        let role = msg.role || msg.sender;
        if (role === 'assistant') {
          role = 'ai';
        }
        
        return {
          role: role,
          content: msg.content || msg.text,
          contentType: msg.content_type || msg.message_type || 'text',
          sentAt: msg.sent_at || msg.timestamp,
          metadata: msg.metadata
        };
      });
    } catch (err) {
      console.error('❌ Error loading conversation:', err);
      throw err;
    }
  }, []);

  /**
   * Refresh conversations list
   */
  const refreshConversations = useCallback(() => {
    return fetchConversations();
  }, [fetchConversations]);

  // Auto-fetch on mount
  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  return {
    conversations,
    loading,
    error,
    fetchConversations,
    loadConversation,
    refreshConversations
  };
}

